/**
 * Pecas das telas da area do cliente (spec 038, secao 3): esqueleto de carregamento, indicador (prazo do teste,
 * proxima mensalidade), percurso (etapas da jornada, andamento de um pedido), bloco recolhivel (modulo da Academy),
 * lista de aulas com o estado de cada uma e a navegacao entre aulas. Nenhuma sabe a marca nem a regra: quem decide o
 * que e urgente, pago ou liberado e o backend, e o app so traduz pra estas pecas.
 */
import { useId, useState, type ReactNode } from "react";
import { IconeBaixo, IconeCadeado, IconeCheck, IconeCheckCirculo, IconeCirculo, IconeFechar, IconePlay, IconeRelogio, IconeSeta, IconeVoltar } from "../icones";
import { LinkInterno } from "../navegacao";
import type { Tom } from "./Basicos";

/** Cartao cinza pulsando no lugar do conteudo que ainda vem (Material: "skeleton"). `linhas` de texto embaixo do titulo. */
export function EsqueletoDeCartao({ linhas = 2, rotulo = "Carregando", barra }: { linhas?: number; rotulo?: string; barra?: boolean }) {
  return (
    <div className="mtc-cartao mtc-esqueleto-cartao" role="status" aria-label={rotulo}>
      <span className="mtc-esqueleto mtc-esqueleto--rotulo" />
      <span className="mtc-esqueleto mtc-esqueleto--titulo" />
      {Array.from({ length: linhas }, (_, i) => <span key={i} className="mtc-esqueleto mtc-esqueleto--texto" />)}
      {barra && <span className="mtc-esqueleto mtc-esqueleto--barra" />}
    </div>
  );
}

/**
 * Um numero que importa agora, com o rotulo em cima e o detalhe embaixo: "44 dias" do teste, "R$ 297,00" da
 * mensalidade. `tom` pinta o cartao inteiro (alerta, erro) quando o backend ou a tela pedem atencao.
 */
export function Indicador({ rotulo, valor, unidade, detalhe, tom = "neutro", icone, children }: { rotulo: string; valor: ReactNode; unidade?: ReactNode; detalhe?: ReactNode; tom?: Extract<Tom, "neutro" | "sucesso" | "alerta" | "erro" | "info">; icone?: ReactNode; children?: ReactNode }) {
  return (
    <section className={`mtc-cartao mtc-indicador mtc-indicador--${tom}`} aria-label={rotulo}>
      <span className="mtc-indicador__rotulo">{icone}{rotulo}</span>
      <span className="mtc-indicador__valor">
        <strong>{valor}</strong>
        {unidade && <span className="mtc-indicador__unidade">{unidade}</span>}
      </span>
      {detalhe && <span className="mtc-indicador__detalhe">{detalhe}</span>}
      {children}
    </section>
  );
}

export type EstadoDoPasso = "feito" | "atual" | "espera" | "futuro" | "erro";
export type PassoDoPercurso = { chave: string; titulo: ReactNode; quando?: ReactNode; detalhe?: ReactNode; estado: EstadoDoPasso };

const LEITURA: Record<EstadoDoPasso, string> = { feito: "concluído", atual: "em andamento", espera: "aguardando", futuro: "a seguir", erro: "não seguiu" };

/**
 * Percurso: os passos de uma jornada, em ordem (as etapas do plano; pedido, aprovado, pago e liberado). Deitado quando
 * a caixa tem 520 px ou mais, em pe no celular (ou sempre, com `emPe`). O estado de cada passo vem pronto do app; o
 * leitor de tela ouve o estado junto do titulo.
 */
export function Percurso({ passos, rotulo, emPe }: { passos: PassoDoPercurso[]; rotulo: string; /** Sempre em pe, qualquer que seja a largura (passos de uma acao dentro de um dialogo). */ emPe?: boolean }) {
  return (
    <div className={`mtc-percurso-caixa${emPe ? " mtc-percurso-caixa--em-pe" : ""}`}>
      <ol className="mtc-percurso" aria-label={rotulo}>
        {passos.map((p, i) => (
          <li key={p.chave} data-estado={p.estado} aria-current={p.estado === "atual" ? "step" : undefined}>
            <span className="mtc-percurso__marca" aria-hidden>
              {p.estado === "feito" ? <IconeCheck tamanho={16} /> : p.estado === "espera" ? <IconeRelogio tamanho={16} /> : p.estado === "erro" ? <IconeFechar tamanho={16} /> : i + 1}
            </span>
            <span className="mtc-percurso__textos">
              <span className="mtc-percurso__titulo">{p.titulo}<span className="mtc-oculto">, {LEITURA[p.estado]}</span></span>
              {p.quando && <span className="mtc-percurso__quando">{p.quando}</span>}
              {p.detalhe && <span className="mtc-percurso__detalhe">{p.detalhe}</span>}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Bloco que abre e fecha (modulo da Academy). O cabecalho inteiro e o botao, com a seta; `lateral` fica do lado do
 * titulo (ex.: o selo "2 de 5"). Fechado, o conteudo sai da arvore de acessibilidade (`hidden`).
 */
export function Recolhivel({ titulo, apoio, lateral, abertoInicial = false, children }: { titulo: ReactNode; apoio?: ReactNode; lateral?: ReactNode; abertoInicial?: boolean; children: ReactNode }) {
  const [aberto, setAberto] = useState(abertoInicial);
  const id = useId();
  return (
    <div className="mtc-recolhivel" data-aberto={aberto || undefined}>
      <button type="button" className="mtc-recolhivel__cabeca" aria-expanded={aberto} aria-controls={id} onClick={() => setAberto(!aberto)}>
        <span className="mtc-recolhivel__textos">
          <span className="mtc-recolhivel__titulo">{titulo}</span>
          {apoio && <span className="mtc-recolhivel__apoio">{apoio}</span>}
        </span>
        {lateral && <span className="mtc-recolhivel__lateral">{lateral}</span>}
        <IconeBaixo className="mtc-recolhivel__seta" tamanho={20} />
      </button>
      <div id={id} className="mtc-recolhivel__corpo" hidden={!aberto}>{children}</div>
    </div>
  );
}

export type EstadoDaAula = "concluida" | "andamento" | "nova" | "fechada";
export type AulaDaLista = { id: string; titulo: string; href?: string; apoio?: string; estado: EstadoDaAula };

const ICONE_DA_AULA: Record<EstadoDaAula, ReactNode> = {
  concluida: <IconeCheckCirculo tamanho={20} />,
  andamento: <IconePlay tamanho={20} />,
  nova: <IconeCirculo tamanho={20} />,
  fechada: <IconeCadeado tamanho={20} />,
};
const ESTADO_DA_AULA: Record<EstadoDaAula, string> = { concluida: "Concluída", andamento: "Em andamento", nova: "Não iniciada", fechada: "Bloqueada" };

/** Aulas de um modulo: o icone diz o estado (concluida, em andamento, nao vista, fechada) e o apoio, duracao e quanto viu. */
export function ListaDeAulas({ aulas, vazio = "Sem aulas publicadas ainda." }: { aulas: AulaDaLista[]; vazio?: string }) {
  if (!aulas.length) return <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{vazio}</p>;
  return (
    <ul className="mtc-aulas">
      {aulas.map((a) => {
        const miolo = (
          <>
            <span className={`mtc-aulas__estado mtc-aulas__estado--${a.estado}`} title={ESTADO_DA_AULA[a.estado]}>{ICONE_DA_AULA[a.estado]}<span className="mtc-oculto">{ESTADO_DA_AULA[a.estado]}: </span></span>
            <span className="mtc-aulas__titulo">{a.titulo}</span>
            {a.apoio && <span className="mtc-aulas__apoio">{a.apoio}</span>}
          </>
        );
        return (
          <li key={a.id}>
            {a.href && a.estado !== "fechada" ? <LinkInterno href={a.href} className="mtc-aulas__item">{miolo}</LinkInterno> : <span className="mtc-aulas__item mtc-aulas__item--fechado">{miolo}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** "Anterior" e "Proxima" no pe de uma aula (ou de qualquer sequencia), com o titulo de cada uma. */
export function AnteriorEProxima({ anterior, proxima, rotulo = "Outras aulas" }: { anterior?: { href: string; titulo: string } | null; proxima?: { href: string; titulo: string } | null; rotulo?: string }) {
  if (!anterior && !proxima) return null;
  return (
    <nav className="mtc-vizinhos" aria-label={rotulo}>
      {anterior ? (
        <LinkInterno href={anterior.href} className="mtc-vizinhos__item">
          <IconeVoltar tamanho={20} />
          <span className="mtc-vizinhos__textos"><span className="mtc-vizinhos__rotulo">Anterior</span><span className="mtc-vizinhos__titulo">{anterior.titulo}</span></span>
        </LinkInterno>
      ) : <span aria-hidden />}
      {proxima && (
        <LinkInterno href={proxima.href} className="mtc-vizinhos__item mtc-vizinhos__item--proxima">
          <span className="mtc-vizinhos__textos"><span className="mtc-vizinhos__rotulo">Próxima</span><span className="mtc-vizinhos__titulo">{proxima.titulo}</span></span>
          <IconeSeta tamanho={20} />
        </LinkInterno>
      )}
    </nav>
  );
}
